import { useQuery } from "@tanstack/react-query";

import { apiGet, qk } from "@/shared/api";

import type { Market, ShowcasePeriod } from "./types";

/**
 * Как часто перезапрашивать метрики открытого экрана робота (доработки §4.2:
 * «рекомендованное обновление UI — 30–60 секунд»).
 *
 * Исключение из глобального `refetchInterval: false` (см. `QueryProvider`),
 * и оно посчитано: рейт-лимит бэкенда — 60 запросов в минуту по IP, экран
 * робота держит два запроса, то есть два обращения в минуту из шестидесяти.
 * Каталог и остальные экраны интервала по-прежнему не имеют — там обновлять
 * нечего в реальном времени.
 *
 * `refetchIntervalInBackground: false` обязателен: Telegram WebView не
 * выгружает вкладку, и без него свёрнутое приложение продолжало бы опрашивать
 * бэкенд сутками.
 */
const METRICS_REFRESH_MS = 60_000;

/**
 * Каталог. `market` необязателен: без него бэкенд отдаёт всех роботов.
 * Список приходит целиком, без курсора — их пять.
 */
export function useRobots(market?: Market) {
  return useQuery({
    queryKey: qk.robots(market ?? null),
    queryFn: ({ signal }) =>
      apiGet("/api/v1/robots", { ...(market ? { query: { market } } : {}), signal }),
  });
}

/**
 * Витринная карточка с графиком на 365 точек.
 *
 * Обновляется раз в минуту, пока экран открыт: в ней живут ключевые метрики
 * §4.1 и состояние лимитированной кампании — заполненность пула и остаток
 * времени, которые §4.2 велит держать свежими.
 */
export function useRobot(id: number) {
  return useQuery({
    queryKey: qk.robot(id),
    queryFn: ({ signal }) => apiGet("/api/v1/robots/{robot_id}", { params: { robot_id: id }, signal }),
    refetchInterval: METRICS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
}

/**
 * Аналитика робота (ТЗ §6–§17) — один запрос на весь экран.
 *
 * Период уезжает в запрос и стоит в ключе кеша: ряд и его прореживание считает
 * бэкенд (правило ТЗ №5), а повторное нажатие на уже открытый период обязано
 * попадать в кеш, а не гонять сеть.
 */
export function useRobotAnalytics(id: number, period: ShowcasePeriod) {
  return useQuery({
    queryKey: qk.robotAnalytics(id, period),
    queryFn: ({ signal }) =>
      apiGet("/api/v1/robots/{robot_id}/analytics", {
        params: { robot_id: id },
        query: { period },
        signal,
      }),
    refetchInterval: METRICS_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
}
